/**
 * Typed testid registry for the shared `components/` bucket — the SINGLE source
 * of truth for every `data-testid` the non-domain application components render
 * (TV1-2055 phase 3, rolling out the TV1-1866 pattern to the web-app tail:
 * `components/`, `pages/`, `contexts/`).
 *
 * Components spread these values into `data-testid`; the LIVE Playwright specs
 * and jsdom unit tests import the SAME object for their selectors. A renamed key
 * changes the string in ONE place and both sides move together — instead of a
 * runtime "element not found" when a component testid drifts from the string a
 * spec hardcoded (e2e is not run in CI, so that drift fails silently). The
 * `teselly/no-raw-testid` ESLint rule forbids raw `data-testid` literals under
 * `src/apps/web-app/**` so new testids must land in a registry like this one.
 *
 * These components sit OUTSIDE any CDSE domain (they are shared / application
 * shell), so a per-domain registry does not fit; the bucket owns them instead.
 * Where two sibling components render the SAME testid value (e.g. WebWidget and
 * MobileWidget both render `widget-error`), they share ONE key here so the
 * single-source property holds across both.
 *
 * Naming convention: `<area>-[section]-[element]` in kebab-case (see
 * `.claude/rules/e2e-testing.md`). Values are preserved verbatim from the
 * pre-existing literals (no rename) so the migration is value-safe; per-row ids
 * are built by the factory functions below.
 */
export const componentsTestIds = {
  // --- AIAssistantChat (chat panel / plan card) ---
  // Previously raw literals under `@components/application/AIAssistantChat/*`;
  // the `aiAssistant` domain registry deliberately excluded them (they live
  // outside the domain dir). They land here now.
  /** User chat bubble (keyed by message id). */
  aiChatMessageUser: ( messageId: string ): string =>
    `ai-assistant-message-user-${ messageId }`,
  /** Assistant chat bubble (keyed by message id). */
  aiChatMessageAssistant: ( messageId: string ): string =>
    `ai-assistant-message-assistant-${ messageId }`,
  /** Streaming "thinking" indicator chip. */
  aiChatStreamingIndicator: 'ai-assistant-streaming-indicator',
  /** Thumbs-up control on one assistant answer (TV1-2417). */
  aiChatFeedbackUp: ( messageId: string ): string =>
    `ai-assistant-feedback-up-${messageId}`,
  /** Thumbs-down control on one assistant answer (TV1-2417). */
  aiChatFeedbackDown: ( messageId: string ): string =>
    `ai-assistant-feedback-down-${messageId}`,
  /** `channel-picker` message block — the reusable "pick a channel" artifact. */
  aiChannelPicker: 'ai-assistant-channel-picker',
  aiChannelPickerOption: ( connectorId: string ): string =>
    `ai-assistant-channel-picker-${ connectorId }`,
  aiChannelPickerConnect: 'ai-assistant-channel-picker-connect',
  /** Secondary action: finish the setup in the Integrations page instead. */
  aiChannelPickerOpenIntegrations: 'ai-assistant-channel-picker-open-integrations',
  /** Store-URL field, shown only for oauth connectors with `settings.needsUrl`. */
  aiChannelPickerStoreUrl: 'ai-assistant-channel-picker-store-url',
  /** Live import-progress artifact attached to an assistant message.
   *  Keyed by KIND (`publications` | `orders`): a channel connection can show two
   *  of these at once, and two nodes sharing one testid make `getByTestId` throw
   *  rather than pick. */
  aiImportProgress: ( kind: string ): string =>
    `ai-assistant-import-progress-${ kind }`,
  aiImportProgressCount: ( kind: string ): string =>
    `ai-assistant-import-progress-${ kind }-count`,
  /** Composed-rule preview artifact attached to an assistant message (TV1-2340). */
  aiRulePreview: 'ai-assistant-rule-preview',
  /** The embedded read-only node graph. */
  aiRulePreviewGraph: 'ai-assistant-rule-preview-graph',
  /** Refusal / unrenderable-graph explanation. */
  aiRulePreviewFailure: 'ai-assistant-rule-preview-failure',
  /** "I corrected my own first attempt" note. */
  aiRulePreviewRepaired: 'ai-assistant-rule-preview-repaired',
  /** The "nothing was created or activated" honesty line. */
  aiRulePreviewNotSaved: 'ai-assistant-rule-preview-not-saved',
  /** Draft-decomposition agreement card (TV1-2382). */
  aiAutomationDraft: 'ai-assistant-automation-draft',
  /** One sub-goal row inside the draft card, keyed by sub-goal id. */
  aiAutomationDraftSubGoal: ( subGoalId: string ): string =>
    `ai-assistant-automation-draft-sub-goal-${ subGoalId }`,
  /** The binding echoed back in the seller's own words, keyed by sub-goal id. */
  aiAutomationDraftBinding: ( subGoalId: string ): string =>
    `ai-assistant-automation-draft-binding-${ subGoalId }`,
  /** The "items added later are not covered" snapshot warning, keyed by sub-goal id. */
  aiAutomationDraftSnapshotWarning: ( subGoalId: string ): string =>
    `ai-assistant-automation-draft-snapshot-${ subGoalId }`,
  /** What one of the seller's references resolved to (TV1-2453), keyed by sub-goal and reference id. */
  aiAutomationDraftReference: ( subGoalId: string, referenceId: string ): string =>
    `ai-assistant-automation-draft-reference-${ subGoalId }-${ referenceId }`,
  /** An open clarification the seller still has to answer. */
  aiAutomationDraftClarification: ( clarificationId: string ): string =>
    `ai-assistant-automation-draft-clarification-${ clarificationId }`,
  /** Submit button. Disabled while anything is unresolved. */
  aiAutomationDraftSubmit: 'ai-assistant-automation-draft-submit',
  /** Why Submit is disabled. */
  aiAutomationDraftBlocked: 'ai-assistant-automation-draft-blocked',
  /** The "nothing exists yet" honesty line. Replaced once the submit is accepted. */
  aiAutomationDraftNotSubmitted: 'ai-assistant-automation-draft-not-submitted',
  /** What replaces it: "the request now exists". Rendered ONLY after `ok: true`. */
  aiAutomationDraftSubmitted: 'ai-assistant-automation-draft-submitted',
  /** The handover link to the request the submit created. */
  aiAutomationDraftRequestLink: 'ai-assistant-automation-draft-request-link',
  /**
   * A server REFUSAL — a resolved 200 carrying `{ ok: false, message }`, rendered
   * in the server's own words. A refusal whose `message` is missing or blank has
   * no words to render, so it never reaches this surface: it degrades to
   * `aiAutomationDraftFailed` (`toSubmitAutomationRequestOutcome`).
   */
  aiAutomationDraftRefused: 'ai-assistant-automation-draft-refused',
  /**
   * Generic failure copy — every call that produced no answer this client can
   * read. That is a submit that THREW, **or a 200 this client cannot narrow**:
   * a body with no `ok` discriminator, or `ok: false` with no usable `message`.
   */
  aiAutomationDraftFailed: 'ai-assistant-automation-draft-failed',
  /** Fullscreen-only conversation rail (ChatGPT/Gemini shape). */
  aiChatSidebar: 'ai-assistant-sidebar',
  aiChatSidebarNewChat: 'ai-assistant-sidebar-new-chat',
  aiChatSidebarItem: ( conversationId: string ): string =>
    `ai-assistant-sidebar-item-${ conversationId }`,
  /** Prompt input. */
  aiChatInput: 'ai-assistant-input',
  /** Send button. */
  aiChatSend: 'ai-assistant-send',
  /** A single command-plan step (keyed by array index). */
  aiChatPlanStep: ( index: number ): string =>
    `ai-assistant-plan-step-${ index }`,
  /** Per-step status pill (keyed by step id). */
  aiChatPlanStatus: ( stepId: string ): string =>
    `ai-assistant-plan-status-${ stepId }`,

  // --- MobileList / MobileListToolbar (TV1-2294) ---
  // Shared mobile list chrome, consumed by the inventory / publications /
  // orders lists. Values match the SDD's data-testid table verbatim.
  /** Sticky command-bar search input. */
  mobileToolbarSearchInput: 'mobile-toolbar-search-input',
  /** "Filters" button that opens the bottom filter sheet. */
  mobileToolbarFiltersButton: 'mobile-toolbar-filters-button',
  /** Active-filter count pill, rendered only when the count is > 0. */
  mobileToolbarFiltersCount: 'mobile-toolbar-filters-count',
  /** A removable active-filter chip (keyed by filter type + option id). */
  mobileFiltersChip: ( filterType: string, id: string ): string =>
    `mobile-filters-chip-${ filterType }-${ id }`,

  // --- MobileList / MobileFilterSheet (TV1-2294) ---
  /** Bottom filter sheet root (the Drawer content element). */
  mobileFiltersSheet: 'mobile-filters-sheet',
  /** "Clear all" header action — disabled while activeFilterCount is 0. */
  mobileFiltersClearAll: 'mobile-filters-clear-all',
  /** Footer "Ver N resultados" button. Dismissal only — filters apply LIVE. */
  mobileFiltersApply: 'mobile-filters-apply',

  // --- MobileList / MobileSelectionBar (TV1-2294) ---
  /** Docked contextual selection bar, rendered only when selectedCount > 0. */
  mobileSelectionBar: 'mobile-selection-bar',
  /** Selected-count label (exclusion-aware count supplied by the domain). */
  mobileSelectionCount: 'mobile-selection-count',
  /** Bulk-actions Menu target. */
  mobileSelectionActions: 'mobile-selection-actions',

  // --- MobileList / MobileListHeader (TV1-2294) ---
  /** Always-visible header row above the mobile cards. */
  mobileListHeader: 'mobile-list-header',
  /**
   * Select-all-PAGE checkbox WRAPPER (`RowSelectCheckbox` takes no testid of
   * its own). Reach the control with
   * `getByTestId( 'mobile-list-header-select-all' ).getByRole( 'checkbox' )`.
   */
  mobileListHeaderSelectAll: 'mobile-list-header-select-all',
  /**
   * The header's PAGE-scoped sentence ("Seleccionar los 10 de esta página" /
   * "10 de esta página seleccionados" / "Todos los resultados están
   * seleccionados"). Ticket 33: it is never the filtered total any more.
   */
  mobileListHeaderLabel: 'mobile-list-header-label',
  /**
   * Explicit "select the whole filtered set" escalation, in the header beside
   * the page checkbox (TV1-2294 ticket 33 moved it off the bottom selection
   * bar). It is a plain `<button>`, so
   * `getByTestId( 'mobile-list-header-select-all-filtered' ).click()` works
   * directly — no `getByRole` hop like the checkbox needs.
   *
   * Rendered ONLY while `isAllPageSelected && !isAllSelected` and the filtered
   * set is bigger than the page, so a spec must select the page first (via
   * `mobileListHeaderSelectAll`) before it exists.
   */
  mobileListHeaderSelectAllFiltered: 'mobile-list-header-select-all-filtered',

  // --- MobileList / density toggle (TV1-2294 ticket 23) ---
  /**
   * Comfortable/compact density toggle, in the header's right slot. It carries
   * `data-density="comfortable" | "compact"`, so a spec asserts the CURRENT
   * state from the attribute rather than from the (localized, action-shaped)
   * label.
   */
  mobileListDensityToggle: 'mobile-list-density-toggle',
  /**
   * Compact-mode row overflow menu TARGET (the kebab). Keyed by row id, because
   * a list renders one per card. Items inside are reached by role/name.
   */
  mobileRowActionsMenu: ( rowId: string ): string =>
    `mobile-row-actions-menu-${ rowId }`,

  // --- SideMenu / mobile tab strip (TV1-2294 ticket 37) ---
  /**
   * The horizontally scrolling tab rail `SideMenu` renders below the `sm`
   * breakpoint when the consumer opts in with `mobileVariant="tabStrip"`.
   * Carries `role="tablist"`; absent on desktop and in the default
   * (`"stacked"`) mobile variant.
   */
  sideMenuMobileTabStrip: 'side-menu-mobile-tab-strip',
  /**
   * A single tab in that rail, keyed by the `SideMenuItem.value` (i.e. the tab
   * id from `NAVIGATION_CONFIG`, e.g. `roles`). It is a real `<button>` with
   * `role="tab"`, so a spec can click it directly and read `aria-selected` /
   * `data-active` for the current state instead of matching a localized label.
   */
  sideMenuMobileTab: ( value: string ): string =>
    `side-menu-mobile-tab-${ value }`,

  // --- Reports / GenerateReportBar ---
  /** Generate-report bar container. */
  generateReportBar: 'generate-report-bar',
  /** Free-text report prompt input. */
  generateReportPrompt: 'generate-report-prompt',
  /** Submit (generate) button. */
  generateReportSubmit: 'generate-report-submit',
  /** Generation-error alert. */
  generateReportError: 'generate-report-error',
  /** Preview banner shown after a report is generated. */
  generateReportPreviewBanner: 'generate-report-preview-banner',
  /** Dismisses the AI prompt panel without generating. */
  generateReportClose: 'generate-report-close',
  /** Discard-preview button. */
  generateReportDiscard: 'generate-report-discard',
  /** Save-report button. */
  generateReportSave: 'generate-report-save',

  // --- Reports / ReportsSidePanel TreeItem options menu ---
  /** Tree-item "..." options trigger. */
  treeItemOptions: 'tree-item-options',
  /** Rename menu item. */
  treeItemRename: 'tree-item-rename',
  /** Duplicate menu item. */
  treeItemDuplicate: 'tree-item-duplicate',
  /** Delete menu item. */
  treeItemDelete: 'tree-item-delete',

  // --- Reports / ReportsToolbar ---
  /** Edit-mode toggle (rendered in both the on and off branches). */
  reportsEditToggle: 'reports-edit-toggle',
  /** "Curated" read-only badge. */
  reportsCuratedBadge: 'reports-curated-badge',

  // --- Rules / AppliedRulesList ---
  /** The extract-draft price-from-list rule card. */
  extractDraftPriceFromListRuleCard: 'extract-draft-price-from-list-rule-card',
  /** Extract-draft rule badge. */

  // --- Rules / RuleConfiguration (FormulaInput / TemplateInput / form) ---
  /** Palette section header (keyed by kebab-cased group name). */
  ruleEditorPaletteSection: ( slug: string ): string =>
    `rule-editor-palette-section-${ slug }`,
  /** Palette variable chip (keyed by the chip slug — itemId / source / group). */
  ruleEditorPaletteChip: ( slug: string ): string =>
    `rule-editor-palette-chip-${ slug }`,
  /** Formula "missing item" warning row (keyed by item id). */
  ruleEditorFormulaWarningMissingItem: ( itemId: string ): string =>
    `rule-editor-formula-warning-missing-item-${ itemId }`,
  /** Formula warning banner. */
  ruleEditorFormulaWarningBanner: 'rule-editor-formula-warning-banner',

  // --- SpreadsheetEditor / DataRow ---
  /** A spreadsheet data row (keyed by row id). */
  spreadsheetRow: ( rowId: string ): string => `spreadsheet-row-${ rowId }`,
  /** Per-row error icon (keyed by row id). */
  spreadsheetRowErrorIcon: ( rowId: string ): string =>
    `spreadsheet-row-error-icon-${ rowId }`,

  // --- SpreadsheetEditor / DiffReviewGrid (operation preview) ---
  /** Diff-review grid container. */
  opDiffGrid: 'op-diff-grid',
  /** Approve-all button. */
  opApproveAll: 'op-approve-all',
  /** A diff row (keyed by target id). */
  opDiffRow: ( targetId: string ): string => `op-diff-row-${ targetId }`,
  /** Per-row approve control (keyed by target id). */
  opRowApprove: ( targetId: string ): string => `op-row-approve-${ targetId }`,
  /** A diff cell (keyed by target id + column key). */
  opDiffCell: ( targetId: string, columnKey: string ): string =>
    `op-diff-cell-${ targetId }-${ columnKey }`,

  // --- WidgetLayout (editor / viewer) ---
  /** Editor react-rnd wrapper around a widget (keyed by widget id). */
  widgetRnd: ( widgetId: string ): string => `widget-rnd-${ widgetId }`,
  /** Viewer-mode widget container (keyed by widget id). */
  widgetView: ( widgetId: string ): string => `widget-view-${ widgetId }`,
  /**
   * The editor's read-only note (TV1-2291 · S6). Rendered ONLY when the canvas
   * is narrower than `EDIT_MIN_CANVAS_PX`, in the slot the palette toolbar
   * occupies otherwise — so its presence is also the assertion that edit mode
   * is off (no drag, no resize, no per-card actions, no palette).
   */
  widgetEditorReadOnlyNote: 'widget-editor-read-only-note',
  /**
   * The editor's drop ghost (TV1-2291 · S10). Mounted only DURING a drag/resize,
   * at the RESOLVED landing cell — post-swap, post-compaction — rather than under
   * the pointer. Carries `data-swapping="true"` while a swap is armed; the card
   * it would trade places with carries `data-swap-target="true"`.
   */
  widgetDropGhost: 'widget-drop-ghost',
  /**
   * A card's resize grip (TV1-2291 · S10), keyed by widget id + react-rnd
   * direction (`right` | `bottom` | `bottomRight`). Registered because a resize
   * has to be *drivable* by a test now that it runs through the placement engine
   * — before this the grips were anonymous inline-styled divs and the resize
   * branch of the wiring had no oracle at all.
   */
  widgetResizeHandle: ( widgetId: string, direction: string ): string =>
    `widget-resize-handle-${ direction }-${ widgetId }`,

  // --- WidgetLayout / WidgetCard chrome (TV1-2291 · S12) — the card surface as
  //     the EDITOR draws it, plus its hover action bar. `reports-` prefixed for
  //     the same reason the inspector and the palette are: the chrome belongs to
  //     the Reports editor shell, not to a single widget type.
  //
  //     ⚠️ These are ADDITIONS. `widgetRnd` / `widgetView` above are untouched —
  //     the generate-report E2E spec and `curatedKpiRow.test.tsx` address the
  //     canvas through them, and the `<Rnd>` (position owner) is a different
  //     element from the card (surface owner) in any case.
  /** A card on the EDITOR canvas — the `WidgetCard` root, keyed by widget id. */
  reportsCanvasCard: ( widgetId: string ): string => `reports-canvas-card-${ widgetId }`,
  /** The card's hover action bar: edit. Present only in edit mode. */
  reportsCardEdit: ( widgetId: string ): string => `reports-card-edit-${ widgetId }`,
  /** The card's hover action bar: remove. Present only in edit mode. */
  reportsCardRemove: ( widgetId: string ): string => `reports-card-remove-${ widgetId }`,

  // --- WidgetLayout composition (currently exercised by WidgetLayoutEditor
  //     unit-test mocks that stand in for the real WebWidget / MobileWidget /
  //     toolbar / builder children) ---
  /** Rendered web-widget root (keyed by widget id). */
  webWidget: ( widgetId: string ): string => `web-widget-${ widgetId }`,
  /** Rendered mobile-widget root (keyed by widget id). */
  mobileWidget: ( widgetId: string ): string => `mobile-widget-${ widgetId }`,
  /** Widget toolbar. */
  widgetToolbar: 'widget-toolbar',
  /** Widget builder panel. */
  widgetBuilder: 'widget-builder',

  // --- WidgetLayout / WidgetInspector (TV1-2291 · S16) — the docked right-hand
  //     panel that replaces the WidgetBuilder modal. `reports-` prefixed because
  //     the panel is part of the Reports editor shell, not of a single widget.
  /** The inspector panel root (an <aside>, NOT a Drawer/Modal — no overlay). */
  reportsInspectorPanel: 'reports-inspector-panel',
  /** The visible pane (keyed by `layout` | `card`). */
  reportsInspectorPane: ( pane: string ): string => `reports-inspector-pane-${ pane }`,
  /**
   * The panel's close control. Named `...Back` because it WAS a back button while
   * the panel had three panes to walk between; the panel is now mounted on demand,
   * so the only thing it navigates to is "shut". Kept under the old key so specs
   * that already target it do not churn for a label change.
   */
  reportsInspectorBack: 'reports-inspector-back',
  /**
   * Opens report settings with nothing selected — the ⚙ over the canvas.
   *
   * It exists because the panel is no longer always mounted: the report's own
   * name/description used to live in its default pane, which was permanently on
   * screen. This is the deliberate way back in.
   */
  reportsInspectorOpenSettings: 'reports-inspector-open-settings',
  /** Selected-card title input — edits are live, there is no save round-trip. */
  reportsInspectorTitleInput: 'reports-inspector-title-input',
  /** A card-tint swatch (keyed by the tint token: none | teal | amber | ...). */
  reportsInspectorTint: ( token: string ): string => `reports-inspector-tint-${ token }`,
  /** The free colour picker beside the seven preset swatches. */
  reportsInspectorTintCustom: 'reports-inspector-tint-custom',
  /** The card's author-chosen text colour. */
  /** Step-colour editor: adds a band (or seeds the default three). */
  reportsInspectorColorStepsAdd: 'reports-inspector-color-steps-add',
  /** Step-colour editor: one band's floor. */
  reportsInspectorColorStepFrom: ( index: number ) => `reports-inspector-color-step-from-${ index }`,
  /** Step-colour editor: one band's colour. */
  reportsInspectorColorStepColor: ( index: number ) => `reports-inspector-color-step-color-${ index }`,
  /** Step-colour editor: removes one band. */
  reportsInspectorColorStepRemove: ( index: number ) => `reports-inspector-color-step-remove-${ index }`,
  reportsInspectorTextColor: 'reports-inspector-text-color',
  /** Clears it, handing the card back to the theme's light/dark pair. */
  reportsInspectorTextColorClear: 'reports-inspector-text-color-clear',
  /** The chart SERIES colour editor — the DATA's colours, not the card's. */
  reportsInspectorSeriesColors: 'reports-inspector-series-colors',
  reportsInspectorSeriesColor: ( index: number ): string =>
    `reports-inspector-series-color-${ index }`,
  reportsInspectorSeriesColorRemove: ( index: number ): string =>
    `reports-inspector-series-color-remove-${ index }`,
  reportsInspectorSeriesColorAdd: 'reports-inspector-series-color-add',
  /** The report's card-gutter slider, in the layout pane. */
  reportsInspectorGap: 'reports-inspector-gap',
  /** The 3-dots menu action that points the Dashboard at this report. */
  treeItemSetDashboard: 'tree-item-set-dashboard',
  /** Remove-the-selected-card button. */
  reportsInspectorRemove: 'reports-inspector-remove',
  /** A palette entry that adds a widget (keyed by widget type). */
  reportsInspectorPaletteItem: ( widgetType: string ): string =>
    `reports-inspector-palette-item-${ widgetType }`,
  /** A card-pane configuration section header (keyed by section value). */
  reportsInspectorSection: ( section: string ): string =>
    `reports-inspector-section-${ section }`,

  // --- WidgetLayout / WidgetInspector readmodel source (TV1-2291 · S26) — the
  //     card pane is SOURCE-AWARE, and these are the controls it mounts when the
  //     catalog says the selected card reads a PV3 readmodel rather than Cube.
  /** Readmodel collection select. Changing it repopulates the field list. */
  reportsInspectorCollection: 'reports-inspector-collection',
  /**
   * The notice shown for a collection with NO `-search` route. It is NOT "the
   * base route rejects a filter" (it does not): the base route answers with a
   * bare array carrying no total and pv3 drops `offset`, so the filter and the
   * row cap are evaluated in the browser.
   */
  reportsInspectorClientFilterNotice: 'reports-inspector-client-filter-notice',
  /** Filter-clause field select (keyed by clause index). */
  reportsInspectorFilterField: ( index: number ): string =>
    `reports-inspector-filter-field-${ index }`,
  /** Filter-clause operator select (keyed by clause index). */
  reportsInspectorFilterOp: ( index: number ): string =>
    `reports-inspector-filter-op-${ index }`,
  /** Filter-clause operand input (keyed by clause index; absent for unary ops). */
  reportsInspectorFilterValue: ( index: number ): string =>
    `reports-inspector-filter-value-${ index }`,
  /** Filter-clause remove control (keyed by clause index). */
  reportsInspectorFilterRemove: ( index: number ): string =>
    `reports-inspector-filter-remove-${ index }`,
  /** Add-a-filter-clause button. */
  reportsInspectorFilterAdd: 'reports-inspector-filter-add',
  /** Readmodel row cap. */
  reportsInspectorLimit: 'reports-inspector-limit',
  /** Alert severity select — `alert` is the ONE type whose schema declares it. */
  reportsInspectorSeverity: 'reports-inspector-severity',
  /** Alert deep-link input. Sanitised at RENDER time, never trusted from here. */
  /** The readmodel tile's caption — the line under its number. */
  reportsInspectorCaption: 'reports-inspector-caption',
  reportsInspectorHref: 'reports-inspector-href',

  // --- WidgetLayout / WidgetToolbar floating palette (TV1-2291 · S18) — the
  //     CollapsedBar + PaletteDrawer pair. Kept (it is the interaction the ticket
  //     owner asked to preserve); ticket 18 regrouped it by data source and gave
  //     every type a real label and description. `reports-` prefixed for the same
  //     reason the inspector is: the palette belongs to the Reports editor shell.
  /** The collapsed bar's control that opens the palette drawer. */
  /**
   * A palette GROUP button in the floating bar (keyed by function group).
   *
   * The bar is grouped as of 2026-08-18; the ENTRIES inside each group's menu
   * reuse `reportsPaletteItem` below, which the palette DRAWER already defined for
   * the same thing — one widget type, one testid, whichever surface offered it.
   */
  /**
   * One tile inside a family's HOVER TRAY on the collapsed bar.
   *
   * ⚠️ Deliberately NOT `reportsPaletteItem`, which belongs to the full drawer.
   * The tray tiles are always in the DOM (the reveal is `opacity`, because a
   * `display: none` tray cannot animate and cannot be focused), so sharing an id
   * would make "the drawer is closed" indistinguishable from "the drawer is open"
   * to every query — which is exactly how the toolbar suite started failing when
   * the tray landed.
   */
  reportsPaletteTrayItem: ( type: string ) => `reports-palette-tray-item-${ type }`,
  reportsPaletteGroup: ( group: string ): string => `reports-palette-group-${ group }`,
  /** Opens/closes the AI prompt panel, beside the edit toggle. */
  reportsGenerateToggle: 'reports-generate-toggle',
  reportsPaletteOpen: 'reports-palette-open',
  /** A palette-drawer card that adds a widget (keyed by widget type). */
  reportsPaletteItem: ( widgetType: string ): string =>
    `reports-palette-item-${ widgetType }`,

  // --- Widgets / shared error state (WebWidget + MobileWidget) ---
  /** Widget error container (shared by WebWidget and MobileWidget). */
  widgetError: 'widget-error',
  /** Widget error message (shared by WebWidget and MobileWidget). */
  widgetErrorMessage: 'widget-error-message',
  /** Widget error title (WebWidget error boundary). */
  widgetErrorTitle: 'widget-error-title',
  /**
   * The blank-card placeholder — a card added from the palette and not yet bound
   * to a data source. Distinct from `widgetError` ON PURPOSE: "you have not
   * configured this yet" and "this is broken" are different claims, and the
   * assertion that a freshly added card shows THIS and not the red tile is the
   * regression guard for it.
   */
  widgetUnconfigured: 'widget-unconfigured',
  /** The "Falta: …" line on the placeholder — the fields the card still needs. */
  widgetUnconfiguredReasons: 'widget-unconfigured-reasons',

  // --- Widgets / WebWidget / KpiWidget ---
  /** KPI numeric value. */
  kpiValue: 'kpi-value',
  /** KPI widget container. */
  kpiWidget: 'kpi-widget',
  /** KPI value link (when the KPI links to a target). */
  kpiLink: 'kpi-link',
  /** KPI delta row. */
  kpiDelta: 'kpi-delta',
  /** KPI delta percentage. */
  kpiDeltaPercent: 'kpi-delta-percent',
  /** KPI trend arrow (keyed by direction, e.g. `up` / `down`). */
  kpiArrow: ( direction: string ): string => `kpi-arrow-${ direction }`,
  /** KPI absolute delta. */
  kpiDeltaAbsolute: 'kpi-delta-absolute',
  /** KPI caption. */
  kpiCaption: 'kpi-caption',
  /**
   * TV1-2291 (t15) — the KPI's empty-state block, KEYED BY CAUSE
   * (`kpi-state-prerequisite-missing` / `-empty-period` / `-error` / `-loading`).
   *
   * Keyed rather than shared on purpose: the whole point of the redesign is that
   * the four causes are not interchangeable, so a test (and a reader) must not be
   * able to accept one where another was meant. See `WebWidget/kpiState.ts`.
   */
  kpiState: ( kind: string ): string => `kpi-state-${ kind }`,
  /** The empty-state block's title (what is going on). */
  kpiStateTitle: 'kpi-state-title',
  /** The empty-state block's description (why, in a sentence). */
  kpiStateDescription: 'kpi-state-description',
  /**
   * TV1-2392 (slice 6) — the partial-coverage disclosure: SOME rows behind this
   * KPI resolved an umbral and others did not, so the displayed count (however
   * confident it looks) is not the whole picture. Independent of `kpiState` —
   * it can appear alongside a `ready` number.
   */
  kpiPartialCoverage: 'kpi-partial-coverage',

  // --- Widgets / WebWidget / TableWidget ---
  /** Critical-stock tile with no (sku, warehouse) row below its configured stock threshold. */
  criticalStockWidgetEmpty: 'critical-stock-widget-empty',
  /**
   * TV1-2392 (slice 6) — the same partial-coverage disclosure as
   * `kpiPartialCoverage`, on the Critical-Stock LIST: at least one (sku,
   * warehouse) row in the unfiltered fetch had no umbral resolved.
   */
  criticalStockPartialCoverage: 'critical-stock-partial-coverage',
  /** Table-widget empty state. */
  tableWidgetEmpty: 'table-widget-empty',
  /** Table-widget container. */
  tableWidget: 'table-widget',
  /** Table-widget data row. */
  tableWidgetRow: 'table-widget-row',
  /** Table-widget totals row. */
  tableWidgetTotals: 'table-widget-totals',

  // --- Widgets / WebWidget / TextWidget ---
  /** Text-widget empty state. */
  textWidgetEmpty: 'text-widget-empty',
  /** Text-widget container. */
  textWidget: 'text-widget',

  // --- Widgets / WebWidget / HeadingWidget ---
  /** Heading-widget container (the section title + its rule). */
  headingWidget: 'heading-widget',
  /** Heading-widget placeholder, shown when the widget has no title yet. */
  headingWidgetEmpty: 'heading-widget-empty',

  // --- Widgets / WebWidget / SpacerWidget ---
  /** Spacer-widget container. Present in BOTH modes; empty for a reader. */
  spacerWidget: 'spacer-widget',
  /** Spacer-widget author-only hint. Must NOT exist in view mode. */
  spacerWidgetPlaceholder: 'spacer-widget-placeholder',

  // --- Widgets / WebWidget / readmodel-backed tiles (TV1-2291 · t25) ---
  // Shared non-ready states, rendered by `ReadmodelWidgetStates` for all four types.
  /** "Nothing matches" block — the query ran and no row met the condition. */
  readmodelWidgetEmpty: 'readmodel-widget-empty',
  /** Inline "nothing matches" line beside a MEASURED zero on `count` / `alert`. */
  readmodelWidgetEmptyHint: 'readmodel-widget-empty-hint',
  /** "Data could not be read" block — the payload is not `{ rows, count }`. */
  readmodelWidgetUnavailable: 'readmodel-widget-unavailable',

  /** Count-widget container. */
  countWidget: 'count-widget',
  /** Count-widget number. */
  countWidgetValue: 'count-widget-value',
  /** Count-widget caption. */
  /** The count tile's subject icon — keyed to the readmodel it counts. */
  countWidgetBadge: 'count-widget-badge',
  /** The count tile's optional link wrapper. Absent when the href failed the allow-list. */
  countWidgetLink: 'count-widget-link',
  countWidgetCaption: 'count-widget-caption',

  /** Alert-widget container. Carries `data-severity` and `data-alert-resolved`. */
  alertWidget: 'alert-widget',
  /** Alert-widget number. */
  alertWidgetValue: 'alert-widget-value',
  /**
   * 🔴 Alert-widget deep link. Present ONLY when `sanitizeKpiHref` accepted the
   * authored href — its ABSENCE is the fail-closed assertion for concern C5.
   */
  alertWidgetLink: 'alert-widget-link',
  /** Alert-widget caption. */
  /** The alert tile's subject icon — keyed to the readmodel it counts. */
  alertWidgetBadge: 'alert-widget-badge',
  alertWidgetCaption: 'alert-widget-caption',

  /** List-widget container. */
  listWidget: 'list-widget',
  /** List-widget row. */
  listWidgetRow: 'list-widget-row',
  /** List-widget "+N more" footer — the rows shown are a window, not the total. */
  listWidgetTruncated: 'list-widget-truncated',

  /** Progress-widget container. */
  progressWidget: 'progress-widget',
  /** Progress-widget percentage. ABSENT when the total is 0 (the percentage is ÷0). */
  progressWidgetPercent: 'progress-widget-percent',
  /** Progress-widget "12 of 40" readout. */
  progressWidgetCounts: 'progress-widget-counts',
  /** Progress-widget bar. */
  progressWidgetBar: 'progress-widget-bar',
  /** Progress-widget caption. */
  progressWidgetCaption: 'progress-widget-caption',

  // --- Widgets / WidgetBuilder / DateRange ---
  /** Compare-to-previous-period toggle. */
  dateRangeCompareToggle: 'date-range-compare-toggle',

  // --- wizard (shared multi-step wizard shell) ---
  /** Wizard root container. */
  wizardRoot: 'wizard-root',
  /** Wizard progress rail. */
  wizardProgress: 'wizard-progress',
  /** Cancel button. */
  wizardCancel: 'wizard-cancel',
  /** Back button. */
  wizardBack: 'wizard-back',
  /** Skip button. */
  wizardSkip: 'wizard-skip',
  /** Finish button. */
  wizardFinish: 'wizard-finish',
  /** Next button. */
  wizardNext: 'wizard-next'
} as const;
